import request from '../../utils/request';
import { Result } from '../../models/general';

export interface HeadcountTrendView {
  month: string;
  totalEmployees: number;
  newHires: number;
  departures: number;
  netChange: number;
}

export interface GenderBreakdownView {
  gender: string;
  count: number;
  percentage: number;
}

export interface NationalityBreakdownView {
  nationality: string;
  count: number;
  percentage: number;
}

export interface AgeGroupBreakdownView {
  ageGroup: string;
  count: number;
  percentage: number;
}

export interface DemographicsView {
  genderDistribution: GenderBreakdownView[];
  nationalityDistribution: NationalityBreakdownView[];
  ageDistribution: AgeGroupBreakdownView[];
  totalEmployees: number;
}

export interface TurnoverByDepartmentView {
  department: string;
  totalEmployees: number;
  departures: number;
  turnoverRate: number;
}

export interface ProbationTrackingView {
  inProbationCount: number;
  endingThisMonthCount: number;
  completedCount: number;
  totalActiveCount: number;
}

export interface AssetAllocationView {
  category: string;
  allocated: number;
  available: number;
  underMaintenance: number;
  retired: number;
}

export interface DocumentExpiryByTypeView {
  documentType: string;
  expiringSoon: number;
  urgent: number;
  expired: number;
}

export interface DocumentExpiryView {
  byType: DocumentExpiryByTypeView[];
  totalExpiringSoon: number;
  totalUrgent: number;
  totalExpired: number;
}

export interface ContractExpiryMonthView {
  month: string;
  expiringCount: number;
  category: string;
}

export interface ContractExpiryView {
  byMonth: ContractExpiryMonthView[];
  expiring30Days: number;
  expiring60Days: number;
  totalUpcoming: number;
}

function buildParams(params: Record<string, string | number | null | undefined>): string {
  const entries = Object.entries(params).filter(([, v]) => v != null);
  if (entries.length === 0) return '';
  return '?' + entries.map(([k, v]) => `${k}=${encodeURIComponent(String(v))}`).join('&');
}

export async function getHeadcountTrends(months: number = 6, departmentId?: number | null): Promise<Result<HeadcountTrendView[]>> {
  const params = buildParams({ months, departmentId });
  const response = await request.get<Result<HeadcountTrendView[]>>(`HRAnalytics/HeadcountTrends${params}`);
  return response.data;
}

export async function getDemographics(departmentId?: number | null): Promise<Result<DemographicsView>> {
  const params = buildParams({ departmentId });
  const response = await request.get<Result<DemographicsView>>(`HRAnalytics/Demographics${params}`);
  return response.data;
}

export async function getTurnoverByDepartment(months: number = 12, departmentId?: number | null): Promise<Result<TurnoverByDepartmentView[]>> {
  const params = buildParams({ months, departmentId });
  const response = await request.get<Result<TurnoverByDepartmentView[]>>(`HRAnalytics/TurnoverByDepartment${params}`);
  return response.data;
}

export async function getProbationTracking(departmentId?: number | null): Promise<Result<ProbationTrackingView>> {
  const params = buildParams({ departmentId });
  const response = await request.get<Result<ProbationTrackingView>>(`HRAnalytics/ProbationTracking${params}`);
  return response.data;
}

export async function getAssetAllocation(departmentId?: number | null): Promise<Result<AssetAllocationView[]>> {
  const params = buildParams({ departmentId });
  const response = await request.get<Result<AssetAllocationView[]>>(`HRAnalytics/AssetAllocation${params}`);
  return response.data;
}

export async function getDocumentExpiry(departmentId?: number | null): Promise<Result<DocumentExpiryView>> {
  const params = buildParams({ departmentId });
  const response = await request.get<Result<DocumentExpiryView>>(`HRAnalytics/DocumentExpiry${params}`);
  return response.data;
}

export async function getContractExpiry(departmentId?: number | null): Promise<Result<ContractExpiryView>> {
  const params = buildParams({ departmentId });
  const response = await request.get<Result<ContractExpiryView>>(`HRAnalytics/ContractExpiry${params}`);
  return response.data;
}

export async function exportReport(
  reportType: string,
  format: 'pdf' | 'xlsx',
  months: number = 6,
  departmentId?: number | null
): Promise<Blob> {
  const params = buildParams({ months, departmentId });
  const response = await request.get(`HRAnalytics/Export/${reportType}/${format}${params}`, {
    responseType: 'blob',
  });
  return response.data;
}
